import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  FiCheck,
  FiPrinter,
  FiRefreshCw,
  FiCheckCircle,
  FiFileText,
  FiX,
  FiSend,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../finance/auth/AuthContext';

/**
 * Checklist document definitions matching the official form
 */
const BASE_CHECKLIST = [
  {
    id: 1,
    docKey: 'POLICIES_HANDBOOK',
    title: 'Employee Policies Handbook',
    subtitle: '(Read only)',
    readApplicable: true,
    submitApplicable: false,
  },
  {
    id: 2,
    docKey: 'LEAVE_POLICIES',
    title: 'Employee Leave Policy',
    subtitle: '',
    readApplicable: true,
    submitApplicable: true,
  },
  {
    id: 3,
    docKey: 'UNDERTAKING',
    title: 'Employee Undertaking Form',
    subtitle: '',
    readApplicable: true,
    submitApplicable: true,
  },
  {
    id: 4,
    docKey: 'BANK_ACCOUNT_DETAILS',
    title: 'Bank Account Details Form',
    subtitle: '',
    readApplicable: true,
    submitApplicable: true,
  },
  {
    id: 5,
    docKey: 'EMERGENCY_FORM',
    title: 'Employee Emergency Information Form',
    subtitle: '',
    readApplicable: true,
    submitApplicable: true,
  },
  {
    id: 6,
    docKey: 'AADHAR_COPY',
    title: 'Aadhaar Card Photocopy',
    subtitle: '',
    readApplicable: false,
    submitApplicable: true,
  },
  {
    id: 7,
    docKey: 'PAN_CARD_COPY',
    title: 'PAN Card Photocopy',
    subtitle: '',
    readApplicable: false,
    submitApplicable: true,
  },
];

export default function OnboardingAcknowledgmentModal({
  isOpen,
  onClose,
  onConfirmSubmit,
  docsMetadata = {},
  isSubmitting = false,
  isAlreadySubmitted = false,
}) {
  const { user, username, userId } = useAuth();

  // Initialize checklist items - unchecked (null) by default unless already submitted
  const [checklist, setChecklist] = useState(() =>
    BASE_CHECKLIST.map((item) => ({
      ...item,
      read: isAlreadySubmitted && item.readApplicable ? true : null,
      submitted: isAlreadySubmitted && item.submitApplicable ? true : null,
    }))
  );

  const [employeeName] = useState(
    user?.fullName || username || 'Employee'
  );
  const [employeeId] = useState(
    user?.empId || (userId ? `EMP-${userId}` : '')
  );
  const [designation] = useState(user?.role || 'Team Member');
  const [signDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [declarationConfirmed, setDeclarationConfirmed] = useState(
    isAlreadySubmitted
  );

  // When modal opens, sync states (unchecked by default unless already submitted)
  useEffect(() => {
    if (isOpen) {
      setChecklist(
        BASE_CHECKLIST.map((item) => ({
          ...item,
          read: isAlreadySubmitted && item.readApplicable ? true : null,
          submitted: isAlreadySubmitted && item.submitApplicable ? true : null,
        }))
      );
      setDeclarationConfirmed(Boolean(isAlreadySubmitted));
    }
  }, [isOpen, isAlreadySubmitted]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Compute total questions requiring manual "Yes" confirmation
  const totalApplicableCount = useMemo(() => {
    return checklist.reduce((acc, doc) => {
      let count = 0;
      if (doc.readApplicable) count++;
      if (doc.submitApplicable) count++;
      return acc + count;
    }, 0);
  }, [checklist]);

  // Compute how many questions currently have "Yes" selected
  const currentYesCount = useMemo(() => {
    return checklist.reduce((acc, doc) => {
      let count = 0;
      if (doc.readApplicable && doc.read === true) count++;
      if (doc.submitApplicable && doc.submitted === true) count++;
      return acc + count;
    }, 0);
  }, [checklist]);

  // Strict check: Every single applicable item must be manually marked as YES
  const isAllChecklistYes = totalApplicableCount > 0 && currentYesCount === totalApplicableCount;

  // The submit button is only accessible once all items are YES and final declaration is confirmed
  const canSubmit = isAllChecklistYes && declarationConfirmed && !isSubmitting;

  if (!isOpen) return null;

  // Toggle Read checkbox/radio
  const handleToggleRead = (id, value) => {
    if (isAlreadySubmitted) return;
    setChecklist((prev) =>
      prev.map((doc) => {
        if (doc.id !== id) return doc;
        return { ...doc, read: doc.read === value ? null : value };
      })
    );
  };

  // Toggle Submitted checkbox/radio
  const handleToggleSubmitted = (id, value) => {
    if (isAlreadySubmitted) return;
    setChecklist((prev) =>
      prev.map((doc) => {
        if (doc.id !== id) return doc;
        return { ...doc, submitted: doc.submitted === value ? null : value };
      })
    );
  };

  // Helper: Print
  const handlePrint = () => {
    window.print();
  };

  const handleConfirm = () => {
    if (!isAllChecklistYes) {
      toast.error('You must manually select "Yes" for all checklist items before submitting.');
      return;
    }
    if (!declarationConfirmed) {
      toast.error('Please confirm the final declaration checkbox before submitting.');
      return;
    }
    onConfirmSubmit?.({
      checklist,
      employeeName,
      employeeId,
      designation,
      signDate,
    });
  };

  const modalNode = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-5 bg-slate-950/70 backdrop-blur-xs overflow-y-auto"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 99999,
      }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto max-h-[92vh] animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Modal Header (No-print) ── */}
        <div className="no-print px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center font-bold shrink-0">
              <FiCheckCircle size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Acknowledgment &amp; Onboarding Completion Form
              </h2>
              <p className="text-xs text-slate-500">
                Review and confirm the required checklist before final submission
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors shadow-2xs cursor-pointer"
              title="Print document or save as PDF"
            >
              <FiPrinter size={13} />
              <span>Print / PDF</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors cursor-pointer"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* ── Modal Scrollable Body (The printable form) ── */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 ack-printable-document">
          {/* Company & Document Title Header */}
          <div className="text-center pb-4 border-b border-slate-200">
            <p className="text-[11px] font-bold tracking-widest text-slate-400 uppercase">
              Orakris Med Solutions Pvt. Ltd.
            </p>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mt-0.5">
              Acknowledgment &amp; Onboarding Completion Form
            </h1>
          </div>

          {/* Instructions */}
          <div className="space-y-1">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
              Instructions:
            </h3>
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              Please review the list below and confirm that you have read and/or completed the required
              documents by checking the appropriate boxes.
            </p>
          </div>

          {/* ── Checklist Table ── */}
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-left text-xs sm:text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-800 font-bold">
                  <th className="py-3 px-3 sm:px-4 w-12 text-center">#</th>
                  <th className="py-3 px-3 sm:px-4">Document</th>
                  <th className="py-3 px-3 sm:px-4 text-center w-40 sm:w-48">
                    Read &amp; Understood
                  </th>
                  <th className="py-3 px-3 sm:px-4 text-center w-52 sm:w-64">
                    Printed, Signed, Scanned
                    <br />
                    &amp; Submitted to HR
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-slate-800">
                {checklist.map((doc) => (
                  <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-3.5 px-3 sm:px-4 text-center font-bold text-slate-500">
                      {doc.id}
                    </td>

                    <td className="py-3.5 px-3 sm:px-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{doc.title}</span>
                        {doc.subtitle && (
                          <span className="font-normal italic text-slate-500 text-xs">
                            {doc.subtitle}
                          </span>
                        )}
                        {doc.submitApplicable && (
                          docsMetadata[doc.docKey] ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                              <FiCheck size={10} /> Uploaded
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                              Not uploaded
                            </span>
                          )
                        )}
                      </div>
                    </td>

                    {/* Read */}
                    <td className="py-3.5 px-3 sm:px-4 text-center">
                      {doc.readApplicable ? (
                        <div className="inline-flex items-center justify-center gap-2.5">
                          <label
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs sm:text-sm font-semibold cursor-pointer select-none transition-all ${
                              doc.read === true
                                ? 'bg-blue-50 text-blue-700 border-blue-300 ring-1 ring-blue-400'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={doc.read === true}
                              disabled={isAlreadySubmitted}
                              onChange={() => handleToggleRead(doc.id, true)}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                            <span>Yes</span>
                          </label>

                          <label
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs sm:text-sm font-medium cursor-pointer select-none transition-all ${
                              doc.read === false
                                ? 'bg-rose-50 text-rose-700 border-rose-300 ring-1 ring-rose-400 font-semibold'
                                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={doc.read === false}
                              disabled={isAlreadySubmitted}
                              onChange={() => handleToggleRead(doc.id, false)}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                            />
                            <span>No</span>
                          </label>
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                          N/A
                        </span>
                      )}
                    </td>

                    {/* Submitted */}
                    <td className="py-3.5 px-3 sm:px-4 text-center">
                      {doc.submitApplicable ? (
                        <div className="inline-flex items-center justify-center gap-2.5">
                          <label
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs sm:text-sm font-semibold cursor-pointer select-none transition-all ${
                              doc.submitted === true
                                ? 'bg-blue-50 text-blue-700 border-blue-300 ring-1 ring-blue-400'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={doc.submitted === true}
                              disabled={isAlreadySubmitted}
                              onChange={() => handleToggleSubmitted(doc.id, true)}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                            <span>Yes</span>
                          </label>

                          <label
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs sm:text-sm font-medium cursor-pointer select-none transition-all ${
                              doc.submitted === false
                                ? 'bg-rose-50 text-rose-700 border-rose-300 ring-1 ring-rose-400 font-semibold'
                                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={doc.submitted === false}
                              disabled={isAlreadySubmitted}
                              onChange={() => handleToggleSubmitted(doc.id, false)}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                            />
                            <span>No</span>
                          </label>
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                          N/A
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Final Declaration ── */}
          <div className="space-y-3 pt-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
              Final Declaration
            </h3>
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-200">
              I hereby declare that I have reviewed, completed, and submitted all the above onboarding
              documents as per the instructions provided. I understand their contents and acknowledge
              my responsibility to comply with Orakris Med Solutions Pvt. Ltd.’s policies and procedures.
              I confirm that I am ready to commence my role and begin work with Orakris.
            </p>

            <label className="flex items-start gap-3 cursor-pointer pt-1 select-none">
              <input
                type="checkbox"
                checked={declarationConfirmed}
                disabled={isAlreadySubmitted}
                onChange={(e) => setDeclarationConfirmed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs sm:text-sm font-semibold text-slate-900">
                I acknowledge and confirm this Final Declaration electronically.
              </span>
            </label>
          </div>
        </div>

        {/* ── Modal Footer (No-print) ── */}
        <div className="no-print px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
          >
            {isAlreadySubmitted ? 'Close' : 'Cancel / Review Docs'}
          </button>

          {!isAlreadySubmitted && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 px-5 py-2 text-xs sm:text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              title={
                !canSubmit
                  ? 'Please manually select Yes for all items and confirm the Final Declaration'
                  : 'Submit Final Onboarding'
              }
            >
              {isSubmitting ? (
                <>
                  <FiRefreshCw size={14} className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <FiSend size={14} />
                  <span>Confirm &amp; Submit Final Onboarding</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          body {
            background: white !important;
            color: black !important;
          }
          .no-print, header, nav, aside, footer {
            display: none !important;
          }
          .ack-printable-document {
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
          }
          table {
            border: 1px solid #333 !important;
          }
          th, td {
            border: 1px solid #666 !important;
          }
        }
      `}</style>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalNode, document.body)
    : modalNode;
}
